import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Thread, usePoll } from "@/modules/messaging/Thread";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

export const BADGE_POLL_MS = 25000;

// Unread badge count polled every ~25s; `onNew` fires when the count increases.
export function useUnreadPoll(path, pick, enabled = true, onNew) {
  const [n, setN] = useState(0);
  const prev = useRef(null);
  const newRef = useRef(onNew);
  newRef.current = onNew;
  const load = useCallback(() => {
    if (!enabled) return;
    api.get(path).then((r) => {
      const v = pick(r.data);
      if (prev.current !== null && v > prev.current) newRef.current?.(r.data);
      prev.current = v;
      setN(v);
    }).catch(() => {});
  }, [path, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    window.addEventListener("gf:support-read", load);
    const id = setInterval(load, BADGE_POLL_MS);
    return () => { clearInterval(id); window.removeEventListener("gf:support-read", load); };
  }, [load]);
  return n;
}

const markRead = () => window.dispatchEvent(new Event("gf:support-read"));

export default function SupportChat() {
  const { garage, isManager, access } = useGarage();
  if (!isManager && access.role !== "support") return <EmptyState icon={LifeBuoy} title={t("b9.support.title")} text={t("b9.support.managersOnly")} />;
  return (
    <div data-testid="support-chat-page">
      <PageHeader title={t("b9.support.title")} subtitle={t("b9.support.subtitle")} />
      <div className="gf-card mx-auto max-w-3xl overflow-hidden">
        <Thread path="/support/thread" sendPath="/support/thread/messages" tz={garage.timezone} readOnly={access.role === "support"} onChange={markRead}
          title={t("b9.support.thread")} subtitle={garage.name} emptyText={t("b9.support.empty")} />
      </div>
    </div>
  );
}

function ThreadRow({ th, active, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={`support-thread-${th.garage.slug}`}
      className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50 ${active ? "bg-teal-50" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-slate-900">{th.garage.name}</span>
        {th.unread > 0 && <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`support-unread-${th.garage.slug}`}>{th.unread}</span>}
      </div>
      <p className="truncate text-xs text-slate-500">{th.last_author === "admin" && `${t("msg.you")}: `}{th.last_preview}</p>
      {th.last_message_at && <p className="text-[11px] text-slate-400">{fmtDateTime(th.last_message_at, "Australia/Perth")}</p>}
    </button>
  );
}

export function AdminSupportInbox() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const garages = useFetch("/admin/garages");
  const active = params.get("g");
  const load = useCallback(() => api.get("/admin/support/threads").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), []);
  usePoll(load, 10000);
  const current = (list || []).find((x) => x.garage_id === active) || (garages.data || []).map((g) => ({ garage: g })).find((x) => x.garage.id === active);
  return (
    <div data-testid="admin-support-inbox">
      <PageHeader title={t("b9.support.inbox")} subtitle={t("b9.support.inboxSubtitle")} actions={
        <Select value="" onValueChange={(v) => setParams({ g: v })}>
          <SelectTrigger className="w-60" data-testid="support-start-garage"><SelectValue placeholder={t("b9.support.startWith")} /></SelectTrigger>
          <SelectContent>{(garages.data || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>} />
      <div className="gf-card grid overflow-hidden md:grid-cols-[300px_1fr]">
        <div className={`border-r border-slate-200 ${active ? "hidden md:block" : ""}`} data-testid="support-thread-list">
          {list && list.length === 0 && <div className="p-6"><EmptyState icon={LifeBuoy} title={t("b9.support.noThreads")} /></div>}
          {(list || []).map((th) => <ThreadRow key={th.garage_id} th={th} active={th.garage_id === active} onClick={() => setParams({ g: th.garage_id })} />)}
        </div>
        <div className={active ? "" : "hidden md:block"}>
          {active ? (
            <>
              <div className="flex items-center justify-between px-4 pt-3 text-sm">
                <button type="button" className="text-slate-600 md:hidden" onClick={() => setParams({})} data-testid="support-thread-back">← {t("b9.support.inbox")}</button>
                <Link to={`/admin/garages/${active}`} className="ml-auto text-xs font-semibold text-teal-700" data-testid="support-open-garage">{t("b9.support.openGarage")}</Link>
              </div>
              <Thread key={active} path={`/admin/support/threads/${active}`} sendPath={`/admin/support/threads/${active}/messages`} tz="Australia/Perth"
                onChange={() => { load(); markRead(); }} title={current?.garage?.name || ""} subtitle={t("b9.support.thread")} emptyText={t("b9.support.empty")} />
            </>
          ) : <p className="p-10 text-center text-sm text-slate-500">{t("b9.support.pick")}</p>}
        </div>
      </div>
    </div>
  );
}
